import { Color3, Mesh, MeshBuilder, StandardMaterial, VertexBuffer, type Scene } from '@babylonjs/core';
import { createLogger, resolveTileCenter, type MapBounds, type ThemeFile, type TileMapMetadata, type Vector2, type WorldSpace } from '@sorrem/shared';
import type { MapData } from '../map-loader';
import { applyWorldSpaceUvs, createFloorTilesForRegion } from './floor-tile-mesh';
import { worldSpaceToScenePosition } from './world-to-scene-position';
import { buildWallMeshesWithModels, loadTileCatalog } from './map-meshes-with-models';
import { buildMapProps, type MapPropBuildResult } from './map-props';
import { getMaterialsLoader } from './materials-loader';
import { createWallProfileMesh } from './wall-profile-mesh';
import { isFloorTile } from './wall-cutaway-tiles';

const logger = createLogger('engine:map-meshes');

export interface BuildMapMeshesOptions {
    readonly tileUnitSize: number;
    /** Wall height in tile-units. */
    readonly wallHeightTileUnits?: number;
    /**
     * Builds only the tiles, wall models and props inside this region; the
     * tiles around it still shape its walls. The whole map when left out.
     */
    readonly region?: MapBounds;
}

export interface MapMeshBuildResult {
    /** Every mesh created for this map (ground tiles + walls). */
    readonly meshes: readonly Mesh[];
    /** All ground floor tile meshes (subset of `meshes`). */
    readonly floorMeshes: readonly Mesh[];
    /** All wall meshes (subset of `meshes`). */
    readonly wallMeshes: readonly Mesh[];
    /** Props the crucible was decorated with (instanced, not pickable). */
    readonly props: MapPropBuildResult;
    /** The profile walls' flat tops, which the wall cutaway lowers with a cut wall; null for other walls. */
    readonly wallCaps: WallCapMesh | null;
}

/** Flat wall tops, one quad (four vertices, in order) per wall tile. */
export interface WallCapMesh {
    /** Also in `wallMeshes`. */
    readonly mesh: Mesh;
    /** The wall tile under each quad, in vertex order. */
    readonly tiles: readonly Vector2[];
}

/**
 * Build 3D meshes for a loaded map: floor tile quads, the theme's wall models,
 * profile walls and caps, and props. Positions go through
 * `worldSpaceToScenePosition` (tile X -> scene Z, tile Y -> scene X); the fixed
 * isometric camera does the projection.
 *
 * Materials are loaded from the theme configuration and applied as tiling
 * materials for efficiency (one material per type, not per-tile).
 *
 * @param scene The game scene
 * @param mapData The loaded map data
 * @param themeData The theme configuration (`tileCatalog`, `defaults`, `walls`; see docs/theme-architecture-3d.md)
 * @param metadata The map metadata containing tile info and wall model configs
 * @param options Build options
 */
export async function buildMapMeshes(
    scene: Scene,
    mapData: MapData,
    themeData: ThemeFile,
    metadata: TileMapMetadata,
    options: BuildMapMeshesOptions,
): Promise<MapMeshBuildResult> {
    const { tileUnitSize, wallHeightTileUnits, region } = options;
    const meshes: Mesh[] = [];
    const materialsLoader = getMaterialsLoader();

    // Load tile-catalog to get material metadata. Every theme draws through
    // its catalog: floor material, wall models and floor model all come from it.
    const tileCatalogPath = themeData.tileCatalog;
    if (!tileCatalogPath) {
        throw new Error(`Theme "${themeData.id ?? 'unknown'}" has no tileCatalog; buildMapMeshes needs one.`);
    }
    const tileCatalog: Record<string, { material?: string }> = {};
    try {
        const catalogData = await loadTileCatalog(tileCatalogPath);
        // Extract material for each tile
        for (const [tileName, tileData] of Object.entries(catalogData.tiles ?? {})) {
            if (tileData.material) {
                tileCatalog[tileName] = { material: tileData.material };
            }
        }
    } catch (error) {
        logger.warn('Failed to load tile-catalog:', error);
    }

    // Load floor material from tile-catalog
    let floorMaterial: StandardMaterial | null = null;
    try {
        const floorMeshName = themeData.defaults?.['floor']?.mesh;

        if (floorMeshName) {
            const floorTileMeta = tileCatalog[floorMeshName];
            const floorMaterialId = floorTileMeta?.material;

            if (floorMaterialId) {
                floorMaterial = await materialsLoader.loadMaterial(floorMaterialId, scene);
            }
        }
    } catch (error) {
        logger.warn('Failed to load floor material from tile-catalog:', error);
    }

    if (!floorMaterial) {
        const floorMeshName = themeData.defaults?.['floor']?.mesh;
        throw new Error(
            `Unable to load floor material for theme "${themeData.id ?? 'unknown'}". ` +
            `Tile-catalog entry for mesh "${floorMeshName}" must define a material. ` +
            `Available tiles: ${Object.keys(tileCatalog).join(', ')}`,
        );
    }

    // Create individual floor tile meshes with tiling material
    const floorResult = createFloorTilesForRegion(scene, mapData, {
        tileUnitSize,
        material: floorMaterial,
        region,
    });
    meshes.push(...floorResult.meshes);

    // Wall models from the persisted per-tile assignments (3D auto-tiling).
    const wallMeshResult = await buildWallMeshesWithModels(scene, metadata, themeData, {
        tileUnitSize,
        wallHeightTileUnits,
        floorTiles: themeData.floorModel ? collectFloorTiles(mapData, region ?? mapData.bounds) : undefined,
        region,
    });
    meshes.push(...wallMeshResult.wallMeshes);
    if (wallMeshResult.floorMeshes.length > 0) {
        // The floor model draws the floor; the flat tiles stay for picking only.
        hideForPickingOnly(scene, floorResult.meshes);
        meshes.push(...wallMeshResult.floorMeshes);
    }
    const floorMeshes = [...floorResult.meshes, ...wallMeshResult.floorMeshes];
    const { meshes: profileWalls, material: profileMaterial } = await buildProfileWalls(scene, mapData, themeData, tileUnitSize, region);
    meshes.push(...profileWalls);
    const caps = buildWallCaps(scene, mapData, themeData, tileUnitSize, profileMaterial, region ?? mapData.bounds);
    const capMeshes = caps ? [caps.mesh] : [];
    meshes.push(...capMeshes);
    const props = await buildMapProps(scene, mapData.objectLayers, tileUnitSize, region);
    return {
        meshes,
        floorMeshes,
        wallMeshes: [...wallMeshResult.wallMeshes, ...profileWalls, ...capMeshes],
        props,
        wallCaps: profileMaterial && caps ? caps : null,
    };
}

/** `region` (the whole wall layer when left out) cut to the wall layer. */
function clampToWalls(mapData: MapData, region: MapBounds | undefined): MapBounds {
    return {
        minX: Math.max(0, region?.minX ?? 0),
        minY: Math.max(0, region?.minY ?? 0),
        maxX: Math.min(mapData.wallWidth, region?.maxX ?? mapData.wallWidth),
        maxY: Math.min(mapData.wallHeight, region?.maxY ?? mapData.wallHeight),
    };
}

/** Floor tiles in `region` that are not under a wall, for the theme's floor model. */
function collectFloorTiles(mapData: MapData, region: MapBounds): WorldSpace[] {
    const { bounds } = mapData;
    const tiles: WorldSpace[] = [];
    for (let y = Math.max(bounds.minY, region.minY); y < Math.min(bounds.maxY, region.maxY); y += 1) {
        for (let x = Math.max(bounds.minX, region.minX); x < Math.min(bounds.maxX, region.maxX); x += 1) {
            if (isFloorTile(mapData, x, y)) {
                tiles.push({ x, y });
            }
        }
    }
    return tiles;
}

// Materials made here rather than loaded, one per scene: a map is built a
// chunk at a time, and a material per chunk would pile up, since a map's
// meshes are disposed without their (shared) materials.
const pickOnlyMaterials = new WeakMap<Scene, StandardMaterial>();
const capMaterials = new WeakMap<Scene, Map<string, StandardMaterial>>();

/**
 * Keeps meshes in the scene and pickable but draws nothing for them: they
 * write neither color nor depth. Picking ignores materials, and debug views
 * that toggle `isVisible` on floor meshes still work.
 */
function hideForPickingOnly(scene: Scene, meshes: readonly Mesh[]): void {
    let material = pickOnlyMaterials.get(scene);
    if (!material) {
        material = new StandardMaterial('floor-pick-only', scene);
        material.disableColorWrite = true;
        material.disableDepthWrite = true;
        pickOnlyMaterials.set(scene, material);
    }
    for (const mesh of meshes) {
        mesh.material = material;
    }
}

/** Sweeps the theme's wall profile along every wall face in `region`, when it sets one. */
async function buildProfileWalls(
    scene: Scene,
    mapData: MapData,
    themeData: ThemeFile,
    tileUnitSize: number,
    region: MapBounds | undefined,
): Promise<{ readonly meshes: Mesh[]; readonly material: StandardMaterial | null }> {
    const profile = themeData.walls?.profile;
    if (!profile) {
        return { meshes: [], material: null };
    }
    const material = await getMaterialsLoader().loadMaterial(profile.material, scene);
    if (!material) {
        logger.warn(`Wall profile material "${profile.material}" did not load; walls are untextured`);
    }
    const mesh = createWallProfileMesh(scene, mapData, { tileUnitSize, profile: profile.points, material, region });
    return { meshes: mesh ? [mesh] : [], material };
}

const CAP_THICKNESS = 0.06;

/**
 * Face-drawn walls are panels on the sides of a wall tile, so the tile's top
 * is open. This lays one flat cap over every wall tile in `region` at the
 * theme's wall height, merged into a single mesh. Themes with per-tile wall
 * models need none.
 * With profile walls, the caps take the profile's material, mapped from above
 * in world tiles, so the tops match the plinth and cornice; otherwise they
 * are a flat `capColor`. The merged quads keep their tiles' order, four
 * vertices each, so the wall cutaway can lower one tile's cap.
 */
function buildWallCaps(
    scene: Scene,
    mapData: MapData,
    themeData: ThemeFile,
    tileUnitSize: number,
    profileMaterial: StandardMaterial | null,
    region: MapBounds,
): WallCapMesh | null {
    const walls = themeData.walls;
    if (walls?.mode !== 'faces' || !walls.height) {
        return null;
    }
    const pieces: Mesh[] = [];
    const tiles: Vector2[] = [];
    const wallRegion = clampToWalls(mapData, region);
    for (let y = wallRegion.minY; y < wallRegion.maxY; y += 1) {
        for (let x = wallRegion.minX; x < wallRegion.maxX; x += 1) {
            if ((mapData.wallGids[y * mapData.wallWidth + x] ?? 0) === 0) {
                continue;
            }
            // Profile walls already draw the top edge (the parapet), so their
            // caps are flat tiles; a box's sides would overlap it.
            const cap = profileMaterial
                ? MeshBuilder.CreateGround(`wall-cap-${x}-${y}`, { width: tileUnitSize, height: tileUnitSize }, scene)
                : MeshBuilder.CreateBox(`wall-cap-${x}-${y}`, { width: tileUnitSize, height: CAP_THICKNESS, depth: tileUnitSize }, scene);
            const capY = walls.height * tileUnitSize - (profileMaterial ? 0 : CAP_THICKNESS / 2);
            const center = worldSpaceToScenePosition(resolveTileCenter(x, y), tileUnitSize, capY);
            cap.position.set(center.x, center.y, center.z);
            pieces.push(cap);
            tiles.push({ x, y });
        }
    }
    if (pieces.length === 0) {
        return null;
    }
    const merged = Mesh.MergeMeshes(pieces, true, true);
    if (!merged) {
        throw new Error('buildMapMeshes: failed to merge wall caps');
    }
    merged.name = 'wall-caps';
    if (profileMaterial) {
        applyWorldSpaceUvs(merged, tileUnitSize);
        // Updatable, so the wall cutaway can move a cut tile's cap down.
        merged.setVerticesData(VertexBuffer.PositionKind, merged.getVerticesData(VertexBuffer.PositionKind)!, true);
        merged.material = profileMaterial;
        return { mesh: merged, tiles };
    }
    const capColor = walls.capColor ?? '#3a3530';
    const materials = capMaterials.get(scene) ?? new Map<string, StandardMaterial>();
    capMaterials.set(scene, materials);
    let material = materials.get(capColor);
    if (!material) {
        material = new StandardMaterial('wall-caps', scene);
        material.diffuseColor = Color3.FromHexString(capColor);
        material.specularColor = Color3.Black();
        materials.set(capColor, material);
    }
    merged.material = material;
    return { mesh: merged, tiles };
}
